import React, { useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import get from 'lodash/get';
import { formatPrice } from './utils/price.js';
import { isAnyProductOutOfStock } from './utils/cart.js';
import EmptyMessage from './components/EmptyMessage/index.js';
import Button from './components/Button/index.js';
import { M as MiniShoppingCartContent$1 } from './MiniShoppingCartSkeleton-U6EZ6VaK.js';
import { M as MiniShoppingCartItem } from './MiniShoppingCartItem-UKpWU5t6.js';
import { S as SHOPPING_CART, k as CHECKOUT } from './paths-ZKN3m62_.js';
import { CoreContext } from './context/index.js';

const MiniShoppingCartContent = ({ isLoading, cart, quantity }) => {
    var _a;
    const { t: tCommon } = useTranslation('', { keyPrefix: '$common' });
    const { t: tLabel } = useTranslation('', { keyPrefix: '$common.$labels' });
    const { t: tHeading } = useTranslation('', { keyPrefix: '$common.$headings' });
    const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
    const { t: tValidation } = useTranslation('', { keyPrefix: '$common.$validation' });
    const { t: tCheckout } = useTranslation('', { keyPrefix: '$common.$pages.$checkout' });
    const { data: { siteConfig: { noZeroAfterDecimal = false }, }, } = useContext(CoreContext);
    const router = useRouter();
    const { push } = router;
    const discounts = ((_a = cart === null || cart === void 0 ? void 0 : cart.prices) === null || _a === void 0 ? void 0 : _a.discounts) || [];
    const discountPriceValue = useMemo(() => discounts.reduce((acc, val) => acc + val.amount.value, 0), [discounts]);
    if (isLoading)
        return React.createElement(MiniShoppingCartContent$1, null);
    if (!cart.items.length)
        return React.createElement(EmptyMessage, { text: tValidation('cartEmpty') });
    const isOutOfStock = isAnyProductOutOfStock(cart.items);
    const originalPrice = cart.prices.original_price;
    const currencyCode = originalPrice.currency;
    const locale = router.locale;
    const originalPriceValue = originalPrice.value;
    const subTotalPriceValue = get(cart, 'prices.subtotal_including_tax.value');
    const originalPriceText = formatPrice(originalPriceValue, currencyCode, locale, noZeroAfterDecimal);
    const subTotalPriceText = formatPrice(subTotalPriceValue, currencyCode, locale, noZeroAfterDecimal);
    const discountPriceText = formatPrice(discountPriceValue, currencyCode, locale, noZeroAfterDecimal, true);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "shopping-bag-popup-title" },
            React.createElement("h3", { className: "txt", "aria-label": "Shopping bag pop up." }, tHeading('shoppingCart')),
            React.createElement("div", { className: "quantity", "aria-label": `${quantity} item${quantity > 1 ? 's are' : 'is'} in shopping bag.` },
                quantity,
                " ",
                quantity > 1 ? tLabel('items') : tLabel('item'))),
        React.createElement("div", { className: "shopping-bag-list", tabIndex: 0 }, cart.items.map((item, index) => (React.createElement(MiniShoppingCartItem, { key: item.id, item: item, index: index })))),
        React.createElement("div", { className: "prices" },
            React.createElement("div", { className: "price" },
                React.createElement("div", { className: "price-title" },
                    tLabel('subTotal'),
                    ":"),
                React.createElement("div", { className: "price-value", "aria-label": `Final price is ${subTotalPriceText}` }, subTotalPriceText)),
            Boolean(discountPriceValue) && (React.createElement("div", { className: "price" },
                React.createElement("div", { className: "price-title" },
                    tLabel('discount'),
                    ":"),
                React.createElement("div", { className: "price-value" }, discountPriceText))),
            Boolean(originalPriceValue) && originalPriceValue !== subTotalPriceValue && (React.createElement("div", { className: "price" },
                React.createElement("div", { className: "price-title" },
                    tLabel('originalPrice'),
                    ":"),
                React.createElement("div", { className: "price-value old", "aria-label": `Old price is ${originalPriceText}` }, originalPriceText)))),
        React.createElement("div", { className: "actions" },
            React.createElement(Button, { href: `/${locale}${SHOPPING_CART}`, variant: "plain", className: "actions-btn" }, tButton('toCart')),
            React.createElement(Button, { disabled: isOutOfStock, disabledMsg: React.createElement(React.Fragment, null,
                    React.createElement("span", null, tCommon('outOfStock')),
                    React.createElement("span", null, tCheckout('beforeProceedingCheckout'))), toolTipVariant: "error", tooltipPosition: "top", className: "actions-btn", onClick: () => push(`/${locale}${CHECKOUT}`) }, tButton('toCheckout')))));
};

export { MiniShoppingCartContent as M };
//# sourceMappingURL=MiniShoppingCartContent-tZZmT1oa.js.map
